"use client";
import { useEffect } from "react";
import {
  COUNTRY_CHANGE_EVENT,
  COUNTRY_COOKIE,
  LS_CHECKED_KEY,
  LS_COUNTRY_KEY,
} from "@/lib/country";

const ISO2 = /^[A-Z]{2}$/;

// The same window the old geolocation call used (`maximumAge: 86_400_000`): a
// returning visitor costs nothing, and someone who actually moves country is
// corrected within a day.
const TTL_MS = 86_400_000;

// Shorter window for "we asked and got nothing". A real answer is stable for a
// day; an unknown is more likely to be temporary (a VPN switched off, an IP the
// edge could not place), so retry sooner — but not on every single pageview.
const NEGATIVE_TTL_MS = 21_600_000; // 6h

function readCookie(name: string): string | null {
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

function writeCountryCookie(cc: string) {
  document.cookie = `${COUNTRY_COOKIE}=${cc}; path=/; max-age=31536000; SameSite=Lax`;
}

function readStored(): { country: string | null; checkedAt: number } {
  try {
    const v = window.localStorage.getItem(LS_COUNTRY_KEY);
    return {
      country: v && ISO2.test(v) ? v : null,
      checkedAt: Number(window.localStorage.getItem(LS_CHECKED_KEY)) || 0,
    };
  } catch {
    return { country: null, checkedAt: 0 };
  }
}

/**
 * Resolves the visitor's country from the CDN edge's IP geolocation and
 * publishes it as the `userCountry` cookie.
 *
 * Replaces a `navigator.geolocation.getCurrentPosition()` call this component's
 * predecessor made from a mount effect, on every page, for every visitor, with
 * no UI and no user gesture. Chrome logs an intervention for any geolocation
 * request during page load, and Lighthouse scrapes it out of ConsoleMessages in
 * its `geolocation-on-start` audit — one of the weighted Best Practices audits,
 * and the only one this site was failing. The app only ever needed a two-letter
 * country code: the old code took a GPS fix and immediately reverse-geocoded it
 * away against nominatim.openstreetmap.org, a third party that appeared in no
 * policy page and whose usage terms one call per visitor plainly broke.
 *
 * Asking the edge instead also means localisation now reaches *every* visitor,
 * rather than only the minority who granted an unexplained permission prompt.
 *
 * The downstream contract is unchanged: cookie, localStorage, and a
 * `sticko:country` CustomEvent that components/stickers/LocalizedFeed.tsx
 * already listens for.
 */
export function CountryProbe() {
  useEffect(() => {
    let cancelled = false;

    // Records that we asked, without claiming an answer. Deliberately writes
    // no country and no cookie: "we don't know" must stay distinguishable from
    // a real result, and an absent cookie is the state the static-rendering
    // design depends on.
    function markChecked() {
      if (cancelled) return;
      try {
        window.localStorage.setItem(LS_CHECKED_KEY, String(Date.now()));
      } catch {
        // ignore quota errors
      }
    }

    function apply(cc: string) {
      if (cancelled) return;
      try {
        window.localStorage.setItem(LS_COUNTRY_KEY, cc);
        window.localStorage.setItem(LS_CHECKED_KEY, String(Date.now()));
      } catch {
        // ignore quota errors
      }
      if (readCookie(COUNTRY_COOKIE) !== cc) {
        writeCountryCookie(cc);
        // The feed has usually mounted and read an empty cookie by now, so tell
        // it rather than leaving the visitor on the default feed until their
        // next navigation.
        window.dispatchEvent(new CustomEvent(COUNTRY_CHANGE_EVENT, { detail: cc }));
      }
    }

    // Reinstate the cookie from localStorage first: it costs no network, and
    // the cookie may have been cleared while localStorage survived.
    const { country: stored, checkedAt } = readStored();
    if (stored) apply(stored);
    // A recent check counts even when it produced no country. Before, the
    // early exit required `stored`, and the timestamp was only ever written
    // alongside a country — so everyone /api/country answers `null` for
    // (crawlers, by design; VPN/proxy/satellite IPs; anything the edge cannot
    // place) re-probed on EVERY pageview, forever. With plain <a> navigation
    // every click is a fresh document and a fresh probe, and /api/country is
    // force-dynamic, so each one was an Edge Request plus a Node invocation.
    const ttl = stored ? TTL_MS : NEGATIVE_TTL_MS;
    if (checkedAt && Date.now() - checkedAt < ttl) {
      return () => {
        cancelled = true;
      };
    }

    const controller = new AbortController();
    fetch("/api/country", { signal: controller.signal, headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const cc = typeof data?.country === "string" ? data.country.toUpperCase() : "";
        // Unknown country — local dev, an unroutable IP, or a crawler, which
        // /api/country answers `null` on purpose. Still no country and no
        // cookie: an absent cookie is the state the whole static-rendering
        // design is built on, and it keeps the visitor on the feed the
        // prerendered HTML contains. But record that we asked, so we stop
        // asking again on every single navigation.
        if (ISO2.test(cc)) apply(cc);
        else markChecked();
      })
      .catch(() => {
        // Localisation is an enhancement; the default feed is a complete,
        // working page. Deliberately not console.error — Lighthouse's
        // `errors-in-console` audit fails on error-level entries only, and an
        // aborted fetch on unmount is not worth a Best Practices point.
        //
        // markChecked() is a no-op once `cancelled` is set, and cleanup sets it
        // before calling abort() — so an unmount abort does not burn the
        // negative TTL, while a genuinely failing endpoint stops being retried
        // on every pageview.
        markChecked();
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return null;
}
